import React, { useEffect, useRef, useState } from 'react';

import { Button, Form, InputGroup, Overlay, Popover } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import './GMetrixDateRangePicker.scss';

interface GMetrixDateRangePickerProps {
    disabled?: boolean;
    endDate: Date | null;
    hideLabel?: boolean;
    label?: string;
    maxDate?: Date | null;
    minDate?: Date | null;
    onChange: (startDate: Date | null, endDate: Date | null) => void;
    placeholder?: string;
    startDate: Date | null;
}

export const GMetrixDateRangePicker: React.FC<GMetrixDateRangePickerProps> = ({
    disabled = false,
    endDate,
    hideLabel = false,
    label,
    maxDate = null,
    minDate = null,
    onChange,
    placeholder,
    startDate
}) => {

    const intl = useIntl();

    const [show, setShow] = useState(false);
    const [tempStartDate, setTempStartDate] = useState<Date | null>(startDate);
    const [tempEndDate, setTempEndDate] = useState<Date | null>(endDate);
    const [hoverDate, setHoverDate] = useState<Date | null>(null);
    const [leftMonth, setLeftMonth] = useState(new Date());
    const [rightMonth, setRightMonth] = useState(new Date(new Date().setMonth(new Date().getMonth() + 1)));
    const [focusedDate, setFocusedDate] = useState<Date | null>(null);
    const target = useRef(null);
    const calendarRef = useRef<HTMLDivElement>(null);

    useEffect(() => {

        setTempStartDate(startDate);
        setTempEndDate(endDate);

    }, [startDate, endDate, show]);

    useEffect(() => {

        if (show && calendarRef.current) {

            // Focus the calendar when it opens
            const firstFocusableDate = calendarRef.current.querySelector('.calendar-day:not(.empty)') as HTMLElement;
            if (firstFocusableDate) {

                firstFocusableDate.focus();
                const dateStr = firstFocusableDate.getAttribute('data-date');
                if (dateStr) {

                    setFocusedDate(new Date(dateStr));

                }

            }

        }

    }, [show]);

    const handleToggle = () => {

        if (!disabled) {

            setShow(!show);

        }

    };

    const formatDateRange = () => {

        const shortDateOpts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
        if (!startDate && !endDate) return '';
        if (startDate && !endDate) return `${intl.formatDate(startDate, shortDateOpts)} - `;
        if (!startDate && endDate) return ` - ${intl.formatDate(endDate, shortDateOpts)}`;
        return `${intl.formatDate(startDate!, shortDateOpts)} - ${intl.formatDate(endDate!, shortDateOpts)}`;

    };

    const formatDate = (date: Date) => intl.formatDate(date, { day: '2-digit', month: '2-digit', year: 'numeric' });

    const normalizeDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const isDateDisabled = (date: Date) => {

        const normalizedDate = normalizeDate(date);
        const normalizedMinDate = minDate ? normalizeDate(minDate) : null;
        const normalizedMaxDate = maxDate ? normalizeDate(maxDate) : null;

        if (normalizedMinDate && normalizedDate < normalizedMinDate) {

            return true;

        }

        if (normalizedMaxDate && normalizedDate > normalizedMaxDate) {

            return true;

        }

        return false;

    };

    const clampDateToBounds = (date: Date | null) => {

        if (!date) return null;

        const normalizedDate = normalizeDate(date);
        const normalizedMinDate = minDate ? normalizeDate(minDate) : null;
        const normalizedMaxDate = maxDate ? normalizeDate(maxDate) : null;

        if (normalizedMinDate && normalizedDate < normalizedMinDate) {

            return normalizedMinDate;

        }

        if (normalizedMaxDate && normalizedDate > normalizedMaxDate) {

            return normalizedMaxDate;

        }

        return normalizedDate;

    };

    const handleDateClick = (date: Date) => {

        if (isDateDisabled(date)) {

            return;

        }

        if (!tempStartDate || (tempStartDate && tempEndDate)) {

            // Start new selection
            setTempStartDate(date);
            setTempEndDate(null);

        } else {

            // Complete selection
            if (date < tempStartDate) {

                setTempEndDate(tempStartDate);
                setTempStartDate(date);

            } else {

                setTempEndDate(date);

            }

        }

    };

    const handleApply = () => {

        const nextStartDate = clampDateToBounds(tempStartDate);
        let nextEndDate = clampDateToBounds(tempEndDate);

        if (nextStartDate && nextEndDate && nextStartDate > nextEndDate) {

            nextEndDate = nextStartDate;

        }

        onChange(nextStartDate, nextEndDate);
        setShow(false);

    };

    const handleCancel = () => {

        setTempStartDate(startDate);
        setTempEndDate(endDate);
        setShow(false);

    };

    const handleToday = () => {

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setTempStartDate(today);
        setTempEndDate(today);

        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth();
        const leftYear = leftMonth.getFullYear();
        const leftMonthNum = leftMonth.getMonth();
        const rightYear = rightMonth.getFullYear();
        const rightMonthNum = rightMonth.getMonth();

        const isInView =
            (todayYear === leftYear && todayMonth === leftMonthNum) ||
            (todayYear === rightYear && todayMonth === rightMonthNum);

        if (!isInView) {

            setLeftMonth(new Date(todayYear, todayMonth, 1));
            setRightMonth(new Date(todayYear, todayMonth + 1, 1));

        }

    };

    const handlePrevMonth = () => {

        setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1));
        setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() - 1, 1));

    };

    const handleNextMonth = () => {

        setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1));
        setRightMonth(new Date(rightMonth.getFullYear(), rightMonth.getMonth() + 1, 1));

    };

    const handleKeyDown = (e: React.KeyboardEvent, date: Date) => {

        const key = e.key;
        let newDate: Date | null = null;

        switch (key) {

            case 'Enter':
            case ' ':
                e.preventDefault();
                handleDateClick(date);
                break;
            case 'ArrowLeft':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setDate(date.getDate() - 1);
                break;
            case 'ArrowRight':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setDate(date.getDate() + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setDate(date.getDate() - 7);
                break;
            case 'ArrowDown':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setDate(date.getDate() + 7);
                break;
            case 'Home':
                e.preventDefault();
                newDate = new Date(date.getFullYear(), date.getMonth(), 1);
                break;
            case 'End':
                e.preventDefault();
                newDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
                break;
            case 'PageUp':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setMonth(date.getMonth() - 1);
                break;
            case 'PageDown':
                e.preventDefault();
                newDate = new Date(date);
                newDate.setMonth(date.getMonth() + 1);
                break;
            case 'Escape':
                e.preventDefault();
                handleCancel();
                break;

        }

        if (newDate) {

            setFocusedDate(newDate);

            // Update months if needed
            if (newDate.getMonth() < leftMonth.getMonth() ||
                (newDate.getMonth() === leftMonth.getMonth() && newDate.getFullYear() < leftMonth.getFullYear())) {

                setLeftMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
                setRightMonth(new Date(newDate.getFullYear(), newDate.getMonth() + 1, 1));

            } else if (newDate.getMonth() > rightMonth.getMonth() ||
                (newDate.getMonth() === rightMonth.getMonth() && newDate.getFullYear() > rightMonth.getFullYear())) {

                setLeftMonth(new Date(newDate.getFullYear(), newDate.getMonth() - 1, 1));
                setRightMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));

            }

            // Focus the new date element
            setTimeout(() => {

                const dateElement = calendarRef.current?.querySelector(`[data-date="${newDate!.toISOString()}"]`) as HTMLElement;
                if (dateElement) {

                    dateElement.focus();

                }

            }, 0);

        }

    };

    const handleInputKeyDown = (e: React.KeyboardEvent) => {

        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {

            e.preventDefault();
            setShow(true);

        }

    };

    const isDateInRange = (date: Date) => {

        if (!tempStartDate) return false;
        if (!tempEndDate && !hoverDate) return isSameDay(date, tempStartDate);

        const end = tempEndDate || hoverDate;
        if (!end) return false;

        const start = tempStartDate < end ? tempStartDate : end;
        const actualEnd = tempStartDate < end ? end : tempStartDate;

        return date >= start && date <= actualEnd;

    };

    const isDateStart = (date: Date) => tempStartDate && isSameDay(date, tempStartDate);

    const isDateEnd = (date: Date) => tempEndDate && isSameDay(date, tempEndDate);

    const isSameDay = (date1: Date, date2: Date) => date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear();

    const renderMonth = (month: Date) => {

        const monthName = month.toLocaleString('default', { month: 'long', year: 'numeric' });
        const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
        const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
        const startPadding = firstDay.getDay();
        const days = [];

        // Add empty cells for padding
        for (let i = 0; i < startPadding; i++) {

            days.push(<div className="calendar-day empty" key={`empty-${i}`}></div>);

        }

        // Add day cells
        for (let day = 1; day <= lastDay.getDate(); day++) {

            const date = new Date(month.getFullYear(), month.getMonth(), day);
            const isInRange = isDateInRange(date);
            const isStart = isDateStart(date);
            const isEnd = isDateEnd(date);
            const isToday = isSameDay(date, new Date());
            const isFocused = focusedDate && isSameDay(date, focusedDate);
            const dateStr = date.toISOString();
            const isDisabled = isDateDisabled(date);

            days.push(
                <div
                    aria-disabled={isDisabled}
                    aria-label={`${formatDate(date)}${isToday ? ` (${intl.formatMessage({ id: 'today.211f62df', defaultMessage: 'Today' })})` : ''}${isStart ? ` (${intl.formatMessage({ id: 'start_date.550355c8', defaultMessage: 'Start date' })})` : ''}${isEnd ? ` (${intl.formatMessage({ id: 'end_date.506f89d9', defaultMessage: 'End date' })})` : ''}${isDisabled ? ` (${intl.formatMessage({ id: 'unavailable.94581bf9', defaultMessage: 'Unavailable' })})` : ''}`}
                    aria-pressed={!!(isStart || isEnd)}
                    className={`calendar-day ${isInRange ? 'in-range' : ''} ${isStart ? 'start' : ''} ${isEnd ? 'end' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                    data-date={dateStr}
                    key={day}
                    onClick={() => !isDisabled && handleDateClick(date)}
                    onKeyDown={e => handleKeyDown(e, date)}
                    onMouseEnter={() => !isDisabled && setHoverDate(date)}
                    onMouseLeave={() => !isDisabled && setHoverDate(null)}
                    role="button"
                    tabIndex={!isDisabled && isFocused ? 0 : -1}
                >
                    {day}
                </div>
            );

        }

        return (
            <div aria-label={monthName} className="calendar-month" role="grid">
                <div className="calendar-month-header">
                    {monthName}
                </div>
                <div className="calendar-weekdays" role="row">
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="SU" id="su.701a3e42" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="MO" id="mo.bc907dad" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="TU" id="tu.68c4fc4e" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="WE" id="we.dbae23b4" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="TH" id="th.f41641ed" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="FR" id="fr.c4b0664e" />
                    </div>
                    <div role="columnheader">
                        <FormattedMessage defaultMessage="SA" id="sa.139f9d69" />
                    </div>
                </div>
                <div className="calendar-days" role="rowgroup">
                    {days}
                </div>
            </div>
        );

    };

    return (
        <>
            {!hideLabel && (
                <Form.Label className="fw-bold mb-1">
                    {label ? label : intl.formatMessage({ id: 'date_range.23862b21', defaultMessage: 'Date Range' })}
                </Form.Label>
            )}
            <InputGroup className="gmetrix-daterangepicker-input" ref={target}>
                <Form.Control
                    readOnly
                    aria-expanded={show}
                    aria-haspopup="dialog"
                    aria-label={intl.formatMessage({ id: 'date_range_picker.6d0cfc73', defaultMessage: 'Date range picker' })}
                    disabled={disabled}
                    onClick={handleToggle}
                    onKeyDown={handleInputKeyDown}
                    placeholder={placeholder ?? intl.formatMessage({ id: 'select_date_range.41ecb8e8', defaultMessage: 'Select date range...' })}
                    style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
                    type="text"
                    value={formatDateRange()}
                />
                <Button
                    aria-label={intl.formatMessage({ id: 'open_date_range_picker.32f1ed8c', defaultMessage: 'Open date range picker' })}
                    disabled={disabled}
                    onClick={handleToggle}
                    variant="outline-secondary"
                >
                    <i className="fa-solid fa-calendar"></i>
                </Button>
            </InputGroup>

            <Overlay
                rootClose
                popperConfig={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 6]
                            }
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                altAxis: true,
                                boundary: 'viewport',
                                padding: 12,
                                rootBoundary: 'viewport',
                                tether: true
                            }
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['top-start', 'bottom-end', 'top-end'],
                                padding: 12
                            }
                        }
                    ],
                    strategy: 'absolute'
                }}
                containerPadding={12}
                onHide={handleCancel}
                placement="bottom-start"
                show={show}
                target={target.current}
            >
                <Popover className="gmetrix-daterangepicker-popover">
                    <Popover.Body>
                        <div className="calendar-header">
                            <Button aria-label={intl.formatMessage({ id: 'previous_month.96a254d2', defaultMessage: 'Previous month' })} onClick={handlePrevMonth} size="sm" variant="link">
                                <i className="fa-solid fa-chevron-left"></i>
                            </Button>
                            <Button aria-label={intl.formatMessage({ id: 'go_to_today.eee7bacd', defaultMessage: 'Go to today' })} onClick={handleToday} size="sm" variant="outline-secondary">
                                <FormattedMessage defaultMessage="TODAY" id="today.77439756" />
                            </Button>
                            <Button aria-label={intl.formatMessage({ id: 'next_month.53575336', defaultMessage: 'Next month' })} onClick={handleNextMonth} size="sm" variant="link">
                                <i className="fa-solid fa-chevron-right"></i>
                            </Button>
                        </div>
                        <div aria-label={intl.formatMessage({ id: 'date_range_picker_calendar.d94d3765', defaultMessage: 'Date range picker calendar' })} className="calendar-months" ref={calendarRef} role="application">
                            {renderMonth(leftMonth)}
                            {renderMonth(rightMonth)}
                        </div>
                        <div className="calendar-actions">
                            <Button onClick={handleCancel} size="sm" variant="outline-secondary">
                                <FormattedMessage defaultMessage="Cancel" id="cancel.89a86adc" />
                            </Button>
                            <Button onClick={handleApply} size="sm" variant="primary">
                                <FormattedMessage defaultMessage="Apply" id="apply.6a9ab43f" />
                            </Button>
                        </div>
                    </Popover.Body>
                </Popover>
            </Overlay>
        </>
    );

};
